import { useState } from 'react';
import type { Ctx } from '../ctx';
import { storeSecretCommand } from '../model';
import { Modal } from '../ui';
import type { RegisteredWebhook } from '../types';

// The only place the per-webhook signing secret is ever visible. It arrived in the register/rotate response and lives
// in this component's props until the dialog closes — the api does not keep it, so closing without copying means
// rotating again. The operator stores it in GCP by hand (the exact command is shown), then deploys.
export default function SecretOnce({ ctx, made, onClose }: { ctx: Ctx; made: RegisteredWebhook; onClose: () => void }) {
  const [copied, setCopied] = useState<'secret' | 'cmd' | null>(null);
  const [ack, setAck] = useState(false);
  const rotating = !!made.previousId;
  const cmd = made.secretKey ? storeSecretCommand(ctx.env, made.secretKey, rotating) : '';
  const copy = (what: 'secret' | 'cmd', text: string): void => {
    try {
      void navigator.clipboard?.writeText(text);
    } catch {
      /* clipboard unavailable — the value is selectable */
    }
    setCopied(what);
  };

  return (
    <Modal
      title={rotating ? 'New secret — shown once' : 'Registered — secret shown once'}
      onClose={() => {
        /* no scrim/Esc close: the secret must be acknowledged */
      }}
      actions={
        <>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} aria-label="I have stored the secret" />
            I have copied it somewhere safe
          </label>
          <button type="button" className="btn primary" disabled={!ack} onClick={onClose}>
            Close and forget
          </button>
        </>
      }
    >
      <dl className="kv" style={{ marginBottom: 10 }}>
        <dt>Webhook</dt>
        <dd>
          <code>{made.id}</code> → <code>{made.target}</code>
        </dd>
        {made.previousId ? (
          <>
            <dt>Replaces</dt>
            <dd>
              <code>{made.previousId}</code> <span className="note">(delete it after the new secret is live)</span>
            </dd>
          </>
        ) : null}
        <dt>Fingerprint</dt>
        <dd>
          <code>{made.fingerprint ?? '—'}</code>{' '}
          <span className="note">Settings → Signing secret shows this once the pods carry it</span>
        </dd>
      </dl>
      {made.secretKey ? (
        <>
          <label>
            Signing secret (<code>secretKey</code>)
            <br />
            <span className="row" style={{ gap: 6 }}>
              <input type="text" readOnly aria-label="signing secret" value={made.secretKey} style={{ flex: 1, fontFamily: 'monospace' }} onFocus={(e) => e.currentTarget.select()} />
              <button type="button" className="btn sm" onClick={() => copy('secret', made.secretKey!)}>
                {copied === 'secret' ? 'Copied' : 'Copy'}
              </button>
            </span>
          </label>
          <p className="note" style={{ margin: '10px 0 4px' }}>
            Store it (from the repo root), then deploy — CI on merge, or <code>emit-k8s-secrets.sh --apply</code> + a rollout restart:
          </p>
          <span className="row" style={{ gap: 6 }}>
            <pre style={{ flex: 1, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{cmd}</pre>
            <button type="button" className="btn sm" onClick={() => copy('cmd', cmd)}>
              {copied === 'cmd' ? 'Copied' : 'Copy'}
            </button>
          </span>
          {rotating ? (
            <ol className="note" style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              <li>Store <code>new,current</code> (both verify) → deploy → wait for the fingerprint above to show as live.</li>
              <li>Delete the old registration <code>{made.previousId}</code> (Settings → Delete).</li>
              <li>Trim <code>RW_WEBFLOW_SIGNING_SECRET</code> to the new value alone → deploy.</li>
            </ol>
          ) : (
            <p className="note" style={{ margin: '10px 0 0' }}>
              Until the secret is live the route answers 401 to this hook — the monitor's <code>webhook_401s</code> will say so.
            </p>
          )}
        </>
      ) : (
        <div className="notice">
          The source returned <strong>no secretKey</strong> for this registration. Confirm signing with one real submission
          before relying on it; if the guard rejects, rotate.
        </div>
      )}
    </Modal>
  );
}
